// src/pages/trading-portal/MarketDataIngestion/SingleOptionPayoffPage.js
// Single-leg option (Long Call / Long Put) payoff visualizer - Premium Replica Design Matching Image

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, Alert, Slider, CircularProgress,
    IconButton, Tooltip, Chip, Button,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    RestartAlt as ResetIcon,
    TableChart as TableChartIcon,
    ShowChart as GraphIcon,
    HelpOutline as HelpIcon,
    Sync as SyncIcon,
    Language as LanguageIcon,
    History as HistoryIcon,
    Add as AddIcon,
    FormatListBulleted as ListIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import {
    generatePnLMatrix,
    transformToPercentOfMaxRisk,
    transformToProfitLossPercent,
    transformToContractValue,
} from '../../../utils/blackScholes';

// ─── Design Tokens ──────────────────────────────────────────────────────────

const DARK_BG = '#080c14';           // Deepest background
const DARK_SURFACE = '#0c1222';      // Dashboard body
const DARK_HEADER = '#111827';       // Top bars
const DARK_NAV = '#1a2438';         // Components/Tabs
const DARK_BORDER = '#1e293b';       // Subtle borders
const ACCENT = '#38bdf8';            // Bright blue highlights
const POSITIVE_GREEN = '#22c55e';    // Vivid green
const NEGATIVE_RED = '#ef4444';      // Vivid red
const TEXT_PRIMARY = '#f8fafc';      // White/off-white
const TEXT_SECONDARY = '#94a3b8';    // Muted grey
const TEXT_DIM = '#64748b';          // Dimmer grey/subtext

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const DAY_ABBR = ['S', 'M', 'T', 'W', 'Th', 'F', 'S'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Utility Helpers ─────────────────────────────────────────────────────────

function getCellColor(value, maxPositive, maxNegative) {
    if (maxPositive === 0 && maxNegative === 0) return { bg: DARK_SURFACE, text: TEXT_DIM };

    if (value >= 0) {
        // Linear intensity for green
        const intensity = Math.min(value / (maxPositive || 1), 1);
        // Map intensity to green shades similar to the image
        const r = Math.round(10 + (22 - 10) * intensity);
        const g = Math.round(40 + (197 - 40) * intensity);
        const b = Math.round(20 + (94 - 20) * intensity);
        return {
            bg: `rgb(${r}, ${g}, ${b})`,
            text: intensity > 0.4 ? '#fff' : '#86efac'
        };
    } else {
        // Linear intensity for red
        const intensity = Math.min(Math.abs(value) / (Math.abs(maxNegative) || 1), 1);
        const r = Math.round(35 + (200 - 35) * intensity);
        const g = Math.round(15 + (30 - 15) * (1 - intensity));
        const b = Math.round(15 + (30 - 15) * (1 - intensity));
        return {
            bg: `rgb(${r}, ${g}, ${b})`,
            text: intensity > 0.4 ? '#fff' : '#fca5a5'
        };
    }
}

function formatCellValue(value, viewMode) {
    if (value == null) return '-';
    switch (viewMode) {
        case 'pnl_dollar':
            return value >= 0 ? `${Math.round(value)}` : `${Math.round(value)}`;
        case 'pnl_percent':
        case 'pct_max_risk':
        default:
            return `${Math.round(value)}`;
    }
}

function safeParseDateStr(dateStr) {
    if (!dateStr) return new Date(NaN);
    const datePart = String(dateStr).substring(0, 10);
    return new Date(datePart + 'T12:00:00');
}

function buildMonthGroups(dates) {
    const groups = [];
    let currentMonth = null;
    let currentGroup = null;

    dates.forEach((dateStr) => {
        const d = safeParseDateStr(dateStr);
        const monthKey = `${d.getFullYear()}-${d.getMonth()}`;

        if (monthKey !== currentMonth) {
            currentMonth = monthKey;
            const currentYear = new Date().getFullYear();
            const yearSuffix = d.getFullYear() !== currentYear ? ` '${String(d.getFullYear()).slice(-2)}` : '';
            currentGroup = {
                label: `${MONTH_NAMES[d.getMonth()]}${yearSuffix}`,
                count: 1,
            };
            groups.push(currentGroup);
        } else {
            currentGroup.count++;
        }
    });

    return groups;
}

// ─── Custom Components ───────────────────────────────────────────────────────

/**
 * Expiration Selector: Horizontal scrollable strip of dates
 */
const ExpirationSelector = ({ dates, selected, onSelect }) => {
    const scrollRef = useRef(null);
    const months = useMemo(() => {
        const groups = [];
        let curMonth = null;
        dates.forEach(dStr => {
            const d = safeParseDateStr(dStr);
            const mKey = `${d.getFullYear()}-${d.getMonth()}`;
            if (mKey !== curMonth) {
                curMonth = mKey;
                groups.push({ month: MONTH_NAMES[d.getMonth()], year: d.getFullYear(), startIdx: dates.indexOf(dStr), count: 0 });
            }
            groups[groups.length - 1].count++;
        });
        return groups;
    }, [dates]);

    return (
        <Box sx={{ bgcolor: '#111827', py: 0.5, borderBottom: `1px solid ${DARK_BORDER}`, userSelect: 'none' }}>
            {/* Months Header */}
            <Box sx={{ display: 'flex', borderBottom: `1px solid ${DARK_BORDER}`, mb: 0.5 }}>
                {months.map((m, i) => (
                    <Box key={i} sx={{
                        width: `${(m.count / dates.length) * 100}%`,
                        textAlign: 'center', color: TEXT_SECONDARY,
                        fontSize: '0.65rem', fontWeight: 600, py: 0.2,
                        borderRight: i < months.length - 1 ? `1px solid ${DARK_BORDER}` : 'none'
                    }}>
                        {m.month}{m.year !== new Date().getFullYear() ? ` '${String(m.year).slice(-2)}` : ''}
                    </Box>
                ))}
            </Box>
            {/* Days Row */}
            <Box sx={{
                display: 'flex', overflowX: 'auto', px: 1,
                '&::-webkit-scrollbar': { height: 2 },
                '&::-webkit-scrollbar-thumb': { bgcolor: DARK_BORDER }
            }} ref={scrollRef}>
                {dates.map((dStr) => {
                    const d = safeParseDateStr(dStr);
                    const isSelected = selected === dStr;
                    return (
                        <Box
                            key={dStr}
                            onClick={() => onSelect(dStr)}
                            sx={{
                                minWidth: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                                color: isSelected ? '#fff' : TEXT_SECONDARY,
                                bgcolor: isSelected ? '#38bdf8' : 'transparent',
                                borderRadius: '4px', mx: 0.5, mb: 0.5,
                                '&:hover': { bgcolor: isSelected ? '#38bdf8' : 'rgba(255,255,255,0.05)' }
                            }}
                        >
                            {d.getDate()}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

/**
 * Strike Selector: Custom horizontal ruler with floating marker
 */
const StrikeRuler = ({ strikes, selected, onSelect, underlyingPrice, optionType }) => {
    if (!strikes || strikes.length === 0) return null;

    // Create a dense range for the ruler
    const minS = Math.min(...strikes) * 0.9;
    const maxS = Math.max(...strikes) * 1.1;
    const range = maxS - minS;

    const pct = (val) => ((val - minS) / range) * 100;

    return (
        <Box sx={{ px: 2, pt: 4, pb: 2, borderBottom: `2px solid ${DARK_BORDER}`, bgcolor: DARK_SURFACE, position: 'relative' }}>
            <Typography sx={{ position: 'absolute', left: 16, top: 4, fontSize: '0.7rem', fontWeight: 800, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em' }}>
                STRIKE:
            </Typography>

            <Box sx={{ height: 30, position: 'relative', mb: 2 }}>
                {/* Ruler Line */}
                <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, height: 1.5, bgcolor: DARK_BORDER }} />

                {/* Major Ticks every $10 */}
                {(() => {
                    const ticks = [];
                    const startTick = Math.ceil(minS / 10) * 10;
                    for (let t = startTick; t <= maxS; t += 10) ticks.push(t);
                    return ticks.map(t => (
                        <Box key={t} sx={{ position: 'absolute', left: `${pct(t)}%`, transform: 'translateX(-50%)' }}>
                            <Box sx={{ width: 1.5, height: 8, bgcolor: TEXT_DIM, mx: 'auto' }} />
                            <Typography sx={{ fontSize: '0.65rem', color: TEXT_DIM, mt: 0.5, fontWeight: 600 }}>{t}</Typography>
                        </Box>
                    ));
                })()}

                {/* Minor Ticks */}
                {(() => {
                    const ticks = [];
                    const startTick = Math.ceil(minS / 2) * 2;
                    for (let t = startTick; t <= maxS; t += 2) {
                        if (t % 10 === 0) continue;
                        ticks.push(t);
                    }
                    return ticks.map(t => (
                        <Box key={t} sx={{ position: 'absolute', left: `${pct(t)}%`, bottom: 10, transform: 'translateX(-50%)', width: 1, height: 4, bgcolor: DARK_BORDER }} />
                    ));
                })()}

                {/* Underlying Price Marker (Subtle arrow) */}
                <Box sx={{ position: 'absolute', left: `${pct(underlyingPrice)}%`, bottom: 10, transform: 'translateX(-50%)', zIndex: 1 }}>
                    <Box sx={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '6px solid #94a3b8' }} />
                </Box>

                {/* Selected Strike Floating Label */}
                {selected && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${pct(selected)}%`,
                            top: -24,
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{
                            bgcolor: POSITIVE_GREEN, color: '#fff', fontSize: '0.7rem', fontWeight: 800,
                            px: 1, py: 0.3, borderRadius: '4px', whiteSpace: 'nowrap'
                        }}>
                            {selected}{optionType === 'CALL' ? 'C' : 'P'}
                        </Box>
                        <Box sx={{ width: 2, height: 16, bgcolor: POSITIVE_GREEN }} />
                    </Box>
                )}
            </Box>

            {/* Clickable strike targets (translucent overlay) */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {strikes.map(s => (
                    <Box
                        key={s}
                        onClick={() => onSelect(s)}
                        sx={{
                            position: 'absolute', left: `${pct(s)}%`, width: 40, height: '100%',
                            transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 11,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' }
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

/**
 * PayoffGraph: SVG-based payoff diagram showing expiration + today P&L lines
 */
function PayoffGraph({ prices, matrix, underlyingPrice }) {
    if (!prices || !matrix || prices.length === 0 || matrix.length === 0) return null;

    const width = 900;
    const height = 400;
    const padL = 70, padR = 30, padT = 30, padB = 50;
    const chartW = width - padL - padR;
    const chartH = height - padT - padB;

    const lastColIdx = matrix[0].length - 1;
    const firstColIdx = 0;

    const expirationPayoff = prices.map((_, rowIdx) => matrix[rowIdx][lastColIdx]);
    const currentPayoff = prices.map((_, rowIdx) => matrix[rowIdx][firstColIdx]);

    const allVals = [...expirationPayoff, ...currentPayoff];
    const minVal = Math.min(...allVals);
    const maxVal = Math.max(...allVals);
    const valRange = maxVal - minVal || 1;

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRng = maxPrice - minPrice || 1;

    const toX = (p) => padL + ((p - minPrice) / priceRng) * chartW;
    const toY = (v) => padT + chartH - ((v - minVal) / valRange) * chartH;

    const buildPath = (payoff) => {
        const sortedData = prices.map((p, i) => ({ p, v: payoff[i] })).sort((a, b) => a.p - b.p);
        return sortedData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${toX(d.p).toFixed(1)} ${toY(d.v).toFixed(1)}`).join(' ');
    };

    const sortedExpData = prices.map((p, i) => ({ p, v: expirationPayoff[i] })).sort((a, b) => a.p - b.p);
    const zeroY = toY(0);
    const greenFillPoints = [];
    for (const d of sortedExpData) {
        if (d.v >= 0) greenFillPoints.push(`${toX(d.p).toFixed(1)},${toY(d.v).toFixed(1)}`);
    }

    const yTickCount = 6;
    const yStep = valRange / yTickCount;
    const yTicks = [];
    for (let i = 0; i <= yTickCount; i++) yTicks.push(minVal + yStep * i);

    const xTickCount = 8;
    const xStep = priceRng / xTickCount;
    const xTicks = [];
    for (let i = 0; i <= xTickCount; i++) xTicks.push(minPrice + xStep * i);

    return (
        <Box sx={{ p: 2, overflow: 'auto', bgcolor: DARK_SURFACE, borderRadius: 2 }}>
            <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxHeight: 420, fontFamily }}>
                {/* Grid lines */}
                {yTicks.map((v, i) => (
                    <line key={`yg${i}`} x1={padL} y1={toY(v)} x2={width - padR} y2={toY(v)}
                        stroke={DARK_BORDER} strokeWidth={0.5} />
                ))}

                {/* Zero line */}
                {minVal < 0 && maxVal > 0 && (
                    <line x1={padL} y1={zeroY} x2={width - padR} y2={zeroY}
                        stroke="#475569" strokeWidth={1.5} strokeDasharray="4 3" />
                )}

                {/* Current price vertical line */}
                <line x1={toX(underlyingPrice)} y1={padT} x2={toX(underlyingPrice)} y2={padT + chartH}
                    stroke={ACCENT} strokeWidth={1} strokeDasharray="3 3" />
                <text x={toX(underlyingPrice)} y={padT - 8} textAnchor="middle"
                    fill={ACCENT} fontSize="10" fontWeight="600">
                    ${underlyingPrice.toFixed(0)}
                </text>

                {/* Green fill area (profit at expiration) */}
                {greenFillPoints.length > 1 && (
                    <polygon
                        points={`${greenFillPoints.join(' ')} ${toX(sortedExpData.find((d) => d.v >= 0 && sortedExpData[sortedExpData.indexOf(d) + 1]?.v < 0)?.p || sortedExpData[sortedExpData.length - 1].p).toFixed(1)},${zeroY.toFixed(1)} ${toX(sortedExpData.find((d) => d.v >= 0)?.p || sortedExpData[0].p).toFixed(1)},${zeroY.toFixed(1)}`}
                        fill="rgba(34, 197, 94, 0.12)" />
                )}

                {/* Current date payoff line (dimmer) */}
                <path d={buildPath(currentPayoff)} fill="none"
                    stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2" opacity={0.6} />

                {/* Expiration payoff line (bold) */}
                <path d={buildPath(expirationPayoff)} fill="none"
                    stroke={POSITIVE_GREEN} strokeWidth={2.5} />

                {/* Y-axis labels */}
                {yTicks.map((v, i) => (
                    <text key={`yl${i}`} x={padL - 8} y={toY(v) + 4} textAnchor="end"
                        fill={TEXT_SECONDARY} fontSize="10" fontWeight="500">
                        {v >= 0 ? `$${v.toFixed(0)}` : `-$${Math.abs(v).toFixed(0)}`}
                    </text>
                ))}

                {/* X-axis labels */}
                {xTicks.map((p, i) => (
                    <text key={`xl${i}`} x={toX(p)} y={padT + chartH + 20} textAnchor="middle"
                        fill={TEXT_SECONDARY} fontSize="10" fontWeight="500">
                        ${p.toFixed(0)}
                    </text>
                ))}

                {/* Legend */}
                <line x1={padL + 10} y1={padT + 10} x2={padL + 30} y2={padT + 10}
                    stroke={POSITIVE_GREEN} strokeWidth={2.5} />
                <text x={padL + 35} y={padT + 14} fill={TEXT_SECONDARY} fontSize="10">At Expiration</text>

                <line x1={padL + 130} y1={padT + 10} x2={padL + 150} y2={padT + 10}
                    stroke="#6366f1" strokeWidth={1.5} strokeDasharray="4 2" />
                <text x={padL + 155} y={padT + 14} fill={TEXT_SECONDARY} fontSize="10">Today</text>
            </svg>
        </Box>
    );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const SingleOptionPayoffPage = () => {
    const { symbolId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Data State
    const [symbolData, setSymbolData] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filter/Selection Local State
    const [optionType, setOptionType] = useState(location.state?.optionType || 'CALL');
    const [selectedExp, setSelectedExp] = useState(null);
    const [selectedStrike, setSelectedStrike] = useState(null);
    const [viewMode, setViewMode] = useState('pnl_dollar'); // pnl_dollar, pnl_percent, contract_value, pct_max_risk
    const [displayType, setDisplayType] = useState('table'); // table, graph
    const [priceRange, setPriceRange] = useState([-20, 20]);
    const [ivMultiplier, setIvMultiplier] = useState(1.0);

    // ── Derived Data ─────────────────────────────────────────────────────

    const underlyingPrice = parseFloat(symbolData?.current_price || 0);

    const expDates = useMemo(() => {
        return [...new Set(contracts.filter(c => c.option_type === optionType).map(c => c.expiration_date.substring(0, 10)))].sort();
    }, [contracts, optionType]);

    const availableStrikes = useMemo(() => {
        if (!selectedExp) return [];
        return [...new Set(contracts.filter(c => c.option_type === optionType && c.expiration_date.startsWith(selectedExp)).map(c => c.strike_price))].sort((a, b) => a - b);
    }, [contracts, optionType, selectedExp]);

    const activeContract = useMemo(() => {
        return contracts.find(c =>
            c.option_type === optionType &&
            c.expiration_date.startsWith(selectedExp) &&
            c.strike_price === selectedStrike
        );
    }, [contracts, optionType, selectedExp, selectedStrike]);

    const matrixResult = useMemo(() => {
        if (!activeContract || underlyingPrice <= 0) return null;

        const entryPrice = activeContract.ask || activeContract.last_price || 1.0;

        const leg = {
            side: 'BUY',
            option_type: optionType,
            strike_price: activeContract.strike_price,
            expiration_date: activeContract.expiration_date,
            entry_price: entryPrice,
            quantity: 1,
            implied_volatility: activeContract.implied_volatility || 0.4,
        };

        const today = new Date();
        today.setHours(12, 0, 0, 0);

        return generatePnLMatrix({
            legs: [leg],
            underlyingPrice,
            priceRange: { min: priceRange[0], max: priceRange[1] },
            priceSteps: 22,
            dateRange: { start: today, end: safeParseDateStr(activeContract.expiration_date) },
            riskFreeRate: 0.05,
            ivMultiplier,
        });
    }, [activeContract, underlyingPrice, optionType, priceRange, ivMultiplier]);

    // ── Data Fetching ────────────────────────────────────────────────────

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Get Symbol Info
            const symRes = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            const found = symRes.data.data.find(s => String(s.id) === String(symbolId));
            setSymbolData(found);

            // Get Option Chain
            const optRes = await axiosInstance.get(`/api/market-data-ingestion/options-chain/contracts/${symbolId}?per_page=500`);
            const loadedContracts = optRes.data.data || [];
            setContracts(loadedContracts);

            // Default selections
            if (loadedContracts.length > 0) {
                const initialType = location.state?.optionType || 'CALL';
                const filtered = loadedContracts.filter(c => c.option_type === initialType);
                if (filtered.length > 0) {
                    const firstExp = filtered.sort((a, b) => new Date(a.expiration_date) - new Date(b.expiration_date))[0].expiration_date.substring(0, 10);
                    setSelectedExp(firstExp);

                    const sameExp = filtered.filter(c => c.expiration_date.startsWith(firstExp));
                    if (sameExp.length > 0) {
                        // Find ATM strike
                        const up = parseFloat(found?.current_price || 100);
                        const atm = sameExp.reduce((prev, curr) =>
                            Math.abs(curr.strike_price - up) < Math.abs(prev.strike_price - up) ? curr : prev
                        );
                        setSelectedStrike(atm.strike_price);
                    }
                }
            }
        } catch (err) {
            setError('Failed to load market data');
        } finally {
            setLoading(false);
        }
    }, [symbolId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Effect: Sync strike selection when available strikes change (e.g. expiration changed)
    useEffect(() => {
        if (availableStrikes.length > 0) {
            // Only update if current strike is not in new list or not set
            if (!selectedStrike || !availableStrikes.includes(selectedStrike)) {
                const up = underlyingPrice || 100;
                const atm = availableStrikes.reduce((prev, curr) =>
                    Math.abs(curr - up) < Math.abs(prev - up) ? curr : prev
                );
                setSelectedStrike(atm);
            }
        }
    }, [availableStrikes, underlyingPrice, selectedStrike]);

    const displayMatrix = useMemo(() => {
        if (!matrixResult) return null;
        let data;
        const entryPrice = activeContract?.ask || activeContract?.last_price || 1.0;
        const netCost = entryPrice * 100;

        switch (viewMode) {
            case 'pct_max_risk':
                data = transformToPercentOfMaxRisk(matrixResult.matrix, -netCost);
                break;
            case 'pnl_percent':
                data = transformToProfitLossPercent(matrixResult.matrix, netCost);
                break;
            case 'contract_value':
                data = transformToContractValue(matrixResult.matrix, netCost);
                break;
            case 'pnl_dollar':
            default:
                data = matrixResult.matrix;
                break;
        }

        let maxP = 0, maxN = 0;
        data.forEach(row => row.forEach(v => {
            if (v > maxP) maxP = v;
            if (v < maxN) maxN = v;
        }));

        return { data, maxP, maxN };
    }, [matrixResult, viewMode, activeContract]);

    const monthGroups = useMemo(() => {
        if (!matrixResult?.dates) return [];
        return buildMonthGroups(matrixResult.dates);
    }, [matrixResult?.dates]);

    // ── Header Actions ───────────────────────────────────────────────────

    if (loading) return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;

    const strategyTitle = optionType === 'CALL' ? 'Long Call' : 'Long Put';
    const priceChange = symbolData?.change || 0;
    const priceChangePct = symbolData?.change_percent || 0;

    return (
        <Box sx={{ color: TEXT_PRIMARY, fontFamily }}>

            {/* ── Top App Bar ────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: DARK_HEADER }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.2rem' }}>{strategyTitle}</Typography>
                    <HelpIcon sx={{ fontSize: 18, color: TEXT_DIM, cursor: 'pointer' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" startIcon={<AddIcon />} sx={{ bgcolor: ACCENT, fontSize: '0.7rem', fontWeight: 800, textTransform: 'none' }}>Add</Button>
                    <Button variant="outlined" size="small" startIcon={<ListIcon />} sx={{ color: '#fff', borderColor: DARK_BORDER, fontSize: '0.7rem', fontWeight: 800, textTransform: 'none' }}>Positions (1)</Button>
                    <Button variant="contained" size="small" startIcon={<SaveIcon />} sx={{ bgcolor: '#0ea5e9', fontSize: '0.7rem', fontWeight: 800, textTransform: 'none' }}>Save Trade</Button>
                    <Button variant="outlined" size="small" startIcon={<HistoryIcon />} sx={{ color: '#fff', borderColor: DARK_BORDER, fontSize: '0.7rem', fontWeight: 800, textTransform: 'none' }}>Historical Chart</Button>
                </Box>
            </Box>

            {/* ── Symbol Stats Bar ────────────────────────────────────────── */}
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 3, bgcolor: '#0c1222', borderBottom: `1px solid ${DARK_BORDER}` }}>
                <Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, mb: -0.5 }}>{symbolData?.symbol}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 800 }}>${underlyingPrice.toFixed(2)}</Typography>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: priceChange >= 0 ? POSITIVE_GREEN : NEGATIVE_RED }}>
                            {priceChangePct.toFixed(2)}% {priceChange.toFixed(2)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <SyncIcon sx={{ fontSize: 12, color: TEXT_DIM }} />
                            <Typography sx={{ fontSize: '0.65rem', color: TEXT_DIM, fontWeight: 600 }}>Delayed</Typography>
                            <HelpIcon sx={{ fontSize: 12, color: TEXT_DIM }} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Interaction Area ────────────────────────────────────────── */}
            <Box sx={{ bgcolor: DARK_SURFACE }}>
                <Typography sx={{ px: 2, pt: 1, fontSize: '0.7rem', fontWeight: 800, color: TEXT_SECONDARY }}>
                    EXPIRATION: <span style={{ color: '#fff' }}>{activeContract?.dte || 0}d</span>
                </Typography>

                <ExpirationSelector
                    dates={expDates}
                    selected={selectedExp}
                    onSelect={setSelectedExp}
                />

                <StrikeRuler
                    strikes={availableStrikes}
                    selected={selectedStrike}
                    onSelect={setSelectedStrike}
                    underlyingPrice={underlyingPrice}
                    optionType={optionType}
                />
            </Box>

            {/* ── Summary Stats Row ───────────────────────────────────────── */}
            <Box sx={{
                px: 3, py: 2, display: 'flex', justifyContent: 'space-around',
                bgcolor: DARK_HEADER, borderBottom: `1px solid ${DARK_BORDER}`
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: TEXT_SECONDARY, fontWeight: 700, mb: 0.5 }}>NET DEBIT:</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        💰 ${((activeContract?.ask || 1) * 100).toLocaleString()}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: TEXT_SECONDARY, fontWeight: 700, mb: 0.5 }}>MAX LOSS:</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: NEGATIVE_RED, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        📉 ${((activeContract?.ask || 1) * 100).toLocaleString()}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: TEXT_SECONDARY, fontWeight: 700, mb: 0.5 }}>MAX PROFIT:</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: POSITIVE_GREEN, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        📈 {optionType === 'CALL' ? 'Infinite' : `$${((selectedStrike - (activeContract?.ask || 0)) * 100).toLocaleString()}`}
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: TEXT_SECONDARY, fontWeight: 700, mb: 0.5 }}>CHANCE OF PROFIT:</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: TEXT_DIM }}>--% 🔒</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: TEXT_SECONDARY, fontWeight: 700, mb: 0.5 }}>BREAKEVEN:</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>
                        ➞ {optionType === 'CALL' ? `Above $${(selectedStrike + (activeContract?.ask || 0)).toFixed(2)} (+${(((selectedStrike + (activeContract?.ask || 0) - underlyingPrice) / underlyingPrice) * 100).toFixed(1)}%)` : `Below $${(selectedStrike - (activeContract?.ask || 0)).toFixed(2)}`}
                    </Typography>
                </Box>
            </Box>

            {/* ── Heatmap / Graph Content ────────────────────────────────── */}
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                {displayType === 'graph' && matrixResult ? (
                    <PayoffGraph
                        prices={matrixResult.prices}
                        matrix={matrixResult.matrix}
                        underlyingPrice={underlyingPrice}
                    />
                ) : (
                    <Box sx={{
                        maxHeight: 'calc(100vh - 400px)', overflow: 'auto',
                        '&::-webkit-scrollbar': { width: 6, height: 6 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: DARK_BORDER, borderRadius: 3 }
                    }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
                                {/* Row 1: Month groups */}
                                <tr style={{ background: DARK_HEADER }}>
                                    <th style={{ width: 85, minWidth: 85, border: `1px solid ${DARK_BORDER}`, borderBottom: 'none' }}></th>
                                    {monthGroups.map((group, idx) => (
                                        <th
                                            key={idx}
                                            colSpan={group.count}
                                            style={{
                                                border: `1px solid ${DARK_BORDER}`, borderBottom: 'none',
                                                padding: '4px', textAlign: 'center',
                                                fontSize: '0.72rem', fontWeight: 400, color: TEXT_SECONDARY,
                                                letterSpacing: '0.02em', background: DARK_HEADER
                                            }}
                                        >
                                            {group.label}
                                        </th>
                                    ))}
                                </tr>
                                {/* Row 2: Day number + day-of-week */}
                                <tr style={{ background: DARK_HEADER }}>
                                    <th style={{ width: 85, minWidth: 85, border: `1px solid ${DARK_BORDER}`, background: DARK_HEADER }}></th>
                                    {matrixResult?.dates.map((dStr) => {
                                        const d = safeParseDateStr(dStr);
                                        return (
                                            <th key={dStr} style={{
                                                border: `1px solid ${DARK_BORDER}`, padding: '4px', textAlign: 'center',
                                                fontSize: '0.72rem', fontWeight: 700, color: '#fff', background: DARK_HEADER
                                            }}>
                                                {d.getDate()}{' '}
                                                <span style={{ fontWeight: 400, fontSize: '0.62rem', color: TEXT_SECONDARY }}>
                                                    {DAY_ABBR[d.getDay()]}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {matrixResult?.prices.map((p, rowIdx) => (
                                    <tr key={p}>
                                        <td style={{
                                            border: `1px solid ${DARK_BORDER}`, background: DARK_BG,
                                            padding: '4px', textAlign: 'left', minWidth: 80
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#fff' }}>${Math.round(p)}</Typography>
                                                <Typography sx={{ fontSize: '0.6rem', color: TEXT_DIM, fontWeight: 600 }}>{(((p - underlyingPrice) / underlyingPrice) * 100).toFixed(1)}%</Typography>
                                            </Box>
                                        </td>
                                        {displayMatrix?.data[rowIdx].map((val, colIdx) => {
                                            const styles = getCellColor(val, displayMatrix.maxP, displayMatrix.maxN);
                                            return (
                                                <td
                                                    key={colIdx}
                                                    style={{
                                                        background: styles.bg, color: styles.text,
                                                        border: `0.5px solid ${DARK_BG}`, padding: '6px 2px',
                                                        textAlign: 'center', fontSize: '0.75rem', fontWeight: 700
                                                    }}
                                                >
                                                    {formatCellValue(val, viewMode)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                )}
            </Box>

            {/* ── Range & IV Sliders ────────────────────────────────────── */}
            <Box sx={{
                px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 4,
                bgcolor: DARK_HEADER, borderTop: `1px solid ${DARK_BORDER}`, borderBottom: `1px solid ${DARK_BORDER}`
            }}>
                {/* Range Slider */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: TEXT_SECONDARY, minWidth: 55, letterSpacing: '0.03em' }}>
                        RANGE:
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: TEXT_PRIMARY, minWidth: 50 }}>
                        {`\u00B1${Math.abs(priceRange[1])}%`}
                    </Typography>
                    <Slider
                        value={priceRange}
                        onChange={(_, v) => setPriceRange(v)}
                        min={-50}
                        max={50}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}%`}
                        sx={{
                            color: '#3b82f6',
                            flex: 1,
                            '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: '#fff' },
                            '& .MuiSlider-track': { height: 4 },
                            '& .MuiSlider-rail': { height: 4, bgcolor: DARK_BORDER },
                        }}
                    />
                    <Tooltip title="Reset range">
                        <IconButton size="small" onClick={() => setPriceRange([-20, 20])} sx={{ color: TEXT_DIM }}>
                            <ResetIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* IV Slider */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: TEXT_SECONDARY, minWidth: 160, letterSpacing: '0.03em' }}>
                        IMPLIED VOLATILITY:
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: TEXT_PRIMARY, minWidth: 45 }}>
                        {`${((activeContract?.implied_volatility || 0.3) * ivMultiplier * 100).toFixed(0)}%`}
                    </Typography>
                    <Slider
                        value={ivMultiplier}
                        onChange={(_, v) => setIvMultiplier(v)}
                        min={0.1}
                        max={3.0}
                        step={0.05}
                        marks={[
                            { value: 1, label: '\u00D71' },
                            { value: 2, label: '\u00D72' },
                            { value: 3, label: '\u00D73' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `\u00D7${v.toFixed(1)}`}
                        sx={{
                            color: '#3b82f6',
                            flex: 1,
                            '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: '#fff' },
                            '& .MuiSlider-track': { height: 4 },
                            '& .MuiSlider-rail': { height: 4, bgcolor: DARK_BORDER },
                            '& .MuiSlider-markLabel': { fontFamily, fontSize: '0.65rem', color: TEXT_DIM },
                        }}
                    />
                    <Tooltip title="Reset IV">
                        <IconButton size="small" onClick={() => setIvMultiplier(1.0)} sx={{ color: TEXT_DIM }}>
                            <ResetIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── Bottom Controls ─────────────────────────────────────────── */}
            <Box sx={{
                position: 'fixed', bottom: 0, left: 0, right: 0,
                bgcolor: DARK_NAV, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.5, borderTop: `1px solid ${DARK_BORDER}`, zIndex: 100
            }}>
                <Box sx={{ display: 'flex', bgcolor: DARK_BG, borderRadius: '8px', overflow: 'hidden', mr: 2 }}>
                    <Button
                        size="small"
                        startIcon={<TableChartIcon fontSize="small" />}
                        sx={{ px: 2, color: displayType === 'table' ? '#fff' : TEXT_DIM, bgcolor: displayType === 'table' ? ACCENT : 'transparent', textTransform: 'none', fontWeight: 800, fontSize: '0.7rem' }}
                        onClick={() => setDisplayType('table')}
                    >
                        Table
                    </Button>
                    <Button
                        size="small"
                        startIcon={<GraphIcon fontSize="small" />}
                        sx={{ px: 2, color: displayType === 'graph' ? '#fff' : TEXT_DIM, bgcolor: displayType === 'graph' ? ACCENT : 'transparent', textTransform: 'none', fontWeight: 800, fontSize: '0.7rem' }}
                        onClick={() => setDisplayType('graph')}
                    >
                        Graph
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', bgcolor: DARK_BG, borderRadius: '8px', overflow: 'hidden' }}>
                    {[
                        { id: 'pnl_dollar', label: 'Profit / Loss $' },
                        { id: 'pnl_percent', label: 'Profit / Loss %' },
                        { id: 'contract_value', label: 'Contract Value' },
                        { id: 'pct_max_risk', label: '% of Max Risk' }
                    ].map(btn => (
                        <Button
                            key={btn.id}
                            size="small"
                            onClick={() => setViewMode(btn.id)}
                            sx={{
                                px: 2, borderRight: `1px solid ${DARK_BORDER}`,
                                color: viewMode === btn.id ? '#fff' : TEXT_DIM,
                                bgcolor: viewMode === btn.id ? DARK_BORDER : 'transparent',
                                textTransform: 'none', fontWeight: 700, fontSize: '0.7rem'
                            }}
                        >
                            {btn.label}
                        </Button>
                    ))}
                    <Button size="small" sx={{ px: 2, color: TEXT_DIM, textTransform: 'none', fontWeight: 700, fontSize: '0.7rem' }}>
                        ▾ More
                    </Button>
                </Box>
            </Box>

            {/* Back Button Floating */}
            <IconButton
                onClick={() => navigate(-1)}
                sx={{ position: 'fixed', top: 80, left: 16, bgcolor: 'rgba(255,255,255,0.05)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
                <ArrowBackIcon />
            </IconButton>

        </Box>
    );
};

export default SingleOptionPayoffPage;
